import {
  Component,
  enableProdMode,
  inject,
  Inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { environment } from '../environments/environment';
import { isPlatformBrowser } from '@angular/common';
import { RouterOutlet, RouterModule, Router } from '@angular/router';
import { AlertComponent } from './shared/ui/alert-box/alert-box.component';
import { ToastComponent } from './shared/ui/toast/toast.component';
import { PwaInstallService } from './core/services/pwa-install.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SocketService } from './core/services/socket.service';
import { PushNotificationService } from './core/services/push-notification.service';
import { LayoutComponent } from './shell/layouts/admin-layout/layout.component';
import { SpinnerComponent } from './shared/ui/spinner/spinner.component';
import { AiAssistantPremiumModalComponent } from './shared/ui/ai-assistant-premium-modal/ai-assistant-premium-modal.component';
import { LoadingService } from './core/services/loading.service';
import { NotificationPromptComponent } from './shared/ui/notification-prompt/notification-prompt.component';

import {
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  Event as RouterEvent,
} from '@angular/router';
import { CommonModule } from '@angular/common';
import { I18nWatcherComponent } from './app.translation';
import { HydrationOverlayComponent } from './core/offline/ui/hydration-overlay.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    ToastComponent,
    RouterOutlet,
    RouterModule,
    AlertComponent,
    TranslateModule,
    LayoutComponent,
    AiAssistantPremiumModalComponent,
    SpinnerComponent,
    CommonModule,
    I18nWatcherComponent,
    NotificationPromptComponent,
    HydrationOverlayComponent,
  ],
  providers: [],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent {
  title: string = 'SAM';
  showInstallButton: boolean = false;
  showCancelButton: boolean = false;
  showUpdateNotification: boolean = false;

  private socketService = inject(SocketService);
  private pwaInstallService = inject(PwaInstallService);
  private translate = inject(TranslateService);
  private pushService = inject(PushNotificationService);
  private router = inject(Router);
  public loadingService = inject(LoadingService);

  isLoading = this.loadingService.isLoading;
  isOffline = signal(false);

  constructor(...args: unknown[]);

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    if (environment.production) {
      console.log('Production mode', environment.apiUrl);
      enableProdMode();
    }

    // Translation initialization is now handled by APP_INITIALIZER in app.config.ts
  }

  private handleRouteEvent(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      this.loadingService.show();
    } else if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
      // Small delay for smooth transition — only in browser
      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          this.loadingService.hide();
        }, 500);
      } else {
        this.loadingService.hide();
      }
    }
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      // 1. Migrate legacy user data to the new standardized format
      this.migrateLegacyUserData();

      // Register user for sockets if logged in
      const isLoggedLocal = localStorage.getItem('isLoggedIn') === 'true';
      const isLoggedSession = sessionStorage.getItem('isLoggedIn') === 'true';
      const userData =
        localStorage.getItem('user') || localStorage.getItem('userData');

      if ((isLoggedLocal || isLoggedSession) && userData) {
        try {
          const user = JSON.parse(userData);
          const userId =
            user.id || user.user_id || user.teacher_id || user.userId;
          if (userId) {
            this.socketService.register(userId);

            // Rebind endpoint to current user on every session start
            this.pushService.syncSubscriptionSilent(userId, true);
          }
        } catch (e) {
          console.error('Error parsing user data for socket registration', e);
        }
      } else {
        // Automatically deactivate and clean up push subscriptions on logout/guest state
        this.pushService.unsubscribe();
      }

      // Listen for incoming push notifications (foreground)
      this.pushService.listenForNotifications();

      // Proactive offline detection
      window.addEventListener('offline', () => {
        this.isOffline.set(true);
      });

      window.addEventListener('online', () => {
        this.isOffline.set(false);
      });

      // Subscribe to router events to show/hide loading spinner
      this.router.events.subscribe((event: RouterEvent) => {
        this.handleRouteEvent(event);
      });
    }

    // Subscribe to the install prompt event to show a custom install button
    this.pwaInstallService.installPromptEvent.subscribe(canShowPrompt => {
      this.showInstallButton = canShowPrompt;
      this.showCancelButton = canShowPrompt;
    });

    this.pwaInstallService.upgradeAvailable.subscribe(isAvailable => {
      this.showUpdateNotification = isAvailable;
    });
  }

  // Trigger the install prompt when the button is clicked
  onInstallPwa(): void {
    this.pwaInstallService.showInstallPrompt();
  }

  cancel(): void {
    this.pwaInstallService.cancelInstallPrompt();
    this.showInstallButton = false;
    this.showCancelButton = false;
  }

  reloadApp(): void {
    if (this.showUpdateNotification) {
      this.pwaInstallService
        .activateUpdate()
        .then(() => {
          window.location.reload();
        })
        .catch(() => {
          window.location.reload();
        });
    } else {
      window.location.reload();
    }
  }

  private migrateLegacyUserData(): void {
    // 1. Migrate Teacher Data (snake_case to camelCase)
    const teacherData = localStorage.getItem('user');
    if (teacherData) {
      try {
        const user = JSON.parse(teacherData);
        if (user.teacher_id && !user.id) {
          console.log('Migrating legacy Teacher data...');
          user.id = user.teacher_id;
          user.name = user.teacher_name;
          user.schoolId = user.school_id;
          user.schoolName = user.school_Name;
          user.schoolLogo = user.school_logo;
          user.milestoneType = user.milestone_type;
          user.classCreated = user.class_created;
          user.hasPrograms = user.has_programs;
          user.role = 'Teacher';
          user.isAdmin = false;
          localStorage.setItem('user', JSON.stringify(user));
        }
      } catch (e) {
        console.error('Teacher migration failed', e);
      }
    }

    // 2. Migrate Admin Data (sessionStorage to localStorage + normalization)
    const adminSessionData = sessionStorage.getItem('userData');
    if (adminSessionData) {
      try {
        const user = JSON.parse(adminSessionData);
        if (user.userId && !user.id) {
          console.log('Migrating legacy Admin data to localStorage...');
          user.id = user.userId;
          user.name =
            user.firstName + (user.lastName ? ' ' + user.lastName : '');
          user.schoolId = user.school_id;
          user.schoolName = user.school_Name;
          user.serviceProviderId = user.serviceProviderId;
          user.milestoneType = user.milestone_type;
          user.isAdmin = true;

          localStorage.setItem('userData', JSON.stringify(user));
          localStorage.setItem('isLoggedIn', 'true');
          localStorage.setItem('token', sessionStorage.getItem('token') || '');
          localStorage.setItem('school-id', user.schoolId.toString());
          localStorage.setItem(
            'role',
            user.role || sessionStorage.getItem('role') || 'Admin'
          );

          // Clear legacy session storage
          sessionStorage.removeItem('userData');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('school-id');
          sessionStorage.removeItem('role');
        }
      } catch (e) {
        console.error('Admin migration failed', e);
      }
    }
  }
}
